<script setup lang="ts">
import {
  Building2,
  ChevronRight,
  Download,
  Monitor,
  SlidersHorizontal,
  LogOut,
  Sparkles,
  UserRound,
} from '@lucide/vue'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ toggle: []; close: [] }>()
const analytics = useAnalytics()
const { user, tenant, capabilities } = useAuth()
const trigger = ref<{ element?: HTMLButtonElement }>()
const menuId = useId()
const amelia = ref<{ download: (choose?: boolean) => Promise<void>; pending: boolean }>()
watch(
  () => props.open,
  (open) =>
    analytics.trackEvent('widget_action', {
      widget_type: 'user_menu',
      action: open ? 'open' : 'close',
    }),
)
const initials = computed(() =>
  `${user.value?.firstName[0] ?? ''}${user.value?.lastName[0] ?? ''}`.toLocaleUpperCase(),
)
async function logout() {
  capabilities.value = { commercialLicensing: false }
  try {
    await $fetch('/api/auth/logout', { method: 'POST' })
    analytics.trackEvent('auth_action', { action: 'logout', method: 'session', result: 'success' })
    user.value = null
    analytics.setNavigationSource('user_menu')
    await navigateTo('/login')
  } catch (cause) {
    analytics.trackEvent('operation_failure', {
      domain: 'auth',
      action: 'logout',
      status_category: analyticsStatusCategory(cause),
    })
  }
}
function navigateFromMenu() {
  analytics.setNavigationSource('user_menu')
  emit('close')
}
async function downloadAmelia(choose = false) {
  // Keep focus on the persistent trigger while both download actions are disabled.
  trigger.value?.element?.focus()
  await amelia.value?.download(choose)
}
</script>

<template>
  <div
    v-if="user"
    class="user-menu"
  >
    <UiIconButton
      ref="trigger"
      variant="chrome"
      :aria-controls="menuId"
      class="user-menu-trigger"
      type="button"
      :aria-expanded="open"
      aria-haspopup="menu"
      aria-label="Menu utilisateur"
      @click.stop="emit('toggle')"
    >
      <span>{{ initials }}</span
      ><i aria-hidden="true" />
    </UiIconButton>
    <UiPopover
      :id="menuId"
      :open="open"
      :anchor="trigger?.element"
      label="Menu utilisateur"
      role="menu"
      :width="360"
      class="user-menu-popover analytics-sensitive"
      @close="emit('close')"
    >
      <div class="user-menu-identity">
        <span class="user-menu-avatar">{{ initials }}</span>
        <div>
          <small>Compte connecté</small>
          <strong>{{ user.firstName }} {{ user.lastName }}</strong>
          <span>@{{ user.username }}</span>
        </div>
      </div>
      <div class="user-menu-current-tenant">
        <span class="user-menu-tenant-icon"><Building2 :size="18" /></span>
        <div>
          <small>Entreprise actuelle</small>
          <strong>{{ tenant?.name ?? 'Tenant indisponible' }}</strong>
          <span v-if="tenant?.key">{{ tenant.key }}</span>
        </div>
        <span class="user-menu-tenant-status"><i />Active</span>
      </div>
      <div class="user-menu-links">
        <NuxtLink
          to="/personalization"
          role="menuitem"
          @click="navigateFromMenu"
          ><SlidersHorizontal :size="17" />Personnalisation</NuxtLink
        >
        <NuxtLink
          to="/tenant-select?force=1"
          role="menuitem"
          @click="navigateFromMenu"
          ><Building2 :size="17" />Changer d’entreprise</NuxtLink
        >
        <NuxtLink
          to="/profile"
          role="menuitem"
          @click="navigateFromMenu"
          ><UserRound :size="17" />Mon profil</NuxtLink
        >
        <NuxtLink
          v-if="capabilities.commercialLicensing"
          to="/licenses"
          role="menuitem"
          @click="navigateFromMenu"
          ><UserRound :size="17" />Mes accès</NuxtLink
        >
        <NuxtLink
          to="/changelog"
          role="menuitem"
          @click="navigateFromMenu"
          ><Sparkles :size="17" />Nouveautés</NuxtLink
        >
        <div class="user-menu-amelia">
          <span class="user-menu-amelia-caption">L’application de bureau</span>
          <UiButton
            role="menuitem"
            variant="soft"
            tone="accent"
            class="user-menu-download"
            full-width
            :loading="amelia?.pending"
            @click="downloadAmelia()"
            ><template #leading
              ><Download
                :size="17"
                aria-hidden="true" /></template
            >Télécharger Amélia</UiButton
          >
          <UiButton
            role="menuitem"
            variant="ghost"
            tone="neutral"
            class="user-menu-platforms"
            full-width
            :disabled="amelia?.pending"
            @click="downloadAmelia(true)"
            ><template #leading
              ><Monitor
                :size="17"
                aria-hidden="true" /></template
            >Autres plateformes<template #trailing
              ><ChevronRight
                :size="16"
                aria-hidden="true" /></template
          ></UiButton>
        </div>
        <button
          type="button"
          role="menuitem"
          @click="logout"
        >
          <LogOut :size="17" />Se déconnecter
        </button>
      </div>
    </UiPopover>
    <DashboardAmeliaDownload
      ref="amelia"
      @close-menu="emit('close')"
    />
  </div>
</template>
